import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtErr } from "@/shared/lib/api";
import { Btn, Input, Field } from "@/shared/components/UI";
import Logo from "@/shared/components/Logo";
import {
  Sparkles, CheckCircle2, Clock, MessageSquare, Eye, ArrowLeft,
  FileText, Image as ImageIcon, X, ChevronRight, ChevronDown,
  Palette, PencilRuler, PackageCheck, Truck, Rocket, Trophy, AlertCircle,
  TrendingUp, Layers, Bell, Sun, Camera, AlertTriangle, XCircle, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/shared/lib/auth";
import ClientFloorplan, { ClientViewTabs } from "@/features/client-portal/components/ClientFloorplan";

// Display label for a wall/line: custom name wins, then wall number,
// then the (possibly stale) material_name.
const wallLabel = (l) =>
  (l.wall_name || "").trim() || (l.wall_no || "").trim() || l.material_name || "";

/* ────────────────────────────────────────────────────────────────────────────
   Client Portal — Phase 17a
   A premium, colourful, mobile-first portal for external clients.
   Every external-facing string is plain-English friendly (no ERP jargon).
   ──────────────────────────────────────────────────────────────────────── */

// Colour-coded stage map. Each stage has a pastel BG + strong FG +
// icon so clients can read status at a glance.
const STAGE_CHIP = {
  brewing: {
    label: "Designers crafting your vision into reality", icon: Palette,
    chip: "bg-gradient-to-r from-amber-50 to-orange-50 text-orange-700 border-orange-200",
  },
  design_in_progress: {
    label: "Design in Progress", icon: Palette,
    chip: "bg-blue-100 text-blue-700 border-blue-300",
  },
  pending_approval: {
    label: "Awaiting Your Approval", icon: Eye,
    chip: "bg-amber-100 text-amber-800 border-amber-300",
  },
  modification_requested: {
    label: "Changes Requested", icon: MessageSquare,
    chip: "bg-purple-100 text-purple-700 border-purple-300",
  },
  rejected: {
    label: "Rejected — Designers working on V2", icon: X,
    chip: "bg-rose-100 text-rose-700 border-rose-300",
  },
  approved: {
    label: "Design Approved ✓", icon: CheckCircle2,
    chip: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
  pre_press: {
    label: "Preparing Production Files", icon: FileText,
    chip: "bg-teal-100 text-teal-700 border-teal-300",
  },
  in_production: {
    label: "In Production", icon: Rocket,
    chip: "bg-indigo-100 text-indigo-700 border-indigo-300",
  },
  quality_check: {
    label: "Quality Check", icon: PackageCheck,
    chip: "bg-pink-100 text-pink-700 border-pink-300",
  },
  ready: {
    label: "Ready for Delivery", icon: Truck,
    chip: "bg-lime-100 text-lime-700 border-lime-300",
  },
  completed: {
    label: "Installed & Completed 🎉", icon: Trophy,
    chip: "bg-green-100 text-green-800 border-green-400",
  },
};

// Map a BOQ line's field values → a single client-facing stage key.
function deriveStage(line) {
  const has = (v) => v === "completed" || v === "done";
  // Outsourced lines complete at Final Production File approval — there is NO
  // production / delivery / installation phase visible to the client.
  if (line.production_type === "outsourced") {
    if (line.production_file_status === "approved") return "completed";
    if (line.production_file_status === "modify") return "modification_requested";
    if (line.production_file_status === "rejected") return "rejected";
    if (line.production_file_url) return "pre_press";       // final file shared → awaiting review
    if (has(line.client_design_approval)) return "approved";
    if (line.client_design_approval === "modify") return "modification_requested";
    if (line.client_design_approval === "rejected") return "rejected";
    if (line.client_design_approval === "pending" && line.current_design_file_id) return "pending_approval";
    if (has(line.design_approval)) return "design_in_progress";
    return "brewing";
  }
  if (has(line.installation_progress)) return "completed";
  if (has(line.delivery_progress)) return "ready";
  if (line.production_progress === "in_progress") return "in_production";
  if (has(line.production_progress)) return "quality_check";
  if (has(line.files_received) || has(line.pre_press) || has(line.sample_mockup)) return "pre_press";
  if (has(line.client_design_approval)) return "approved";
  if (line.client_design_approval === "modify") return "modification_requested";
  if (line.client_design_approval === "rejected") return "rejected";
  // 'pending' only means "awaiting you" when a design has actually been shared with you.
  if (line.client_design_approval === "pending" && line.current_design_file_id) return "pending_approval";
  if (has(line.design_approval)) return "design_in_progress";
  // Default: designers haven't shared anything yet → fun brewing message.
  return "brewing";
}

// Helper — "pending your action" only once a design is live; not while brewing.
function needsClientAction(line) {
  return line.client_design_approval === "pending" && !!line.current_design_file_id;
}

// Sort comparator — actionable items first, completed last (used inside groups).
function rankLine(l) {
  const s = deriveStage(l);
  return s === "pending_approval" ? 0
    : s === "rejected" || s === "modification_requested" ? 1
    : s === "completed" ? 99 : 10;
}

// Msg 623 — cluster BOQ lines by their `wall_group` sub-scope label so the
// client portal mirrors the Designer/Pre-Audit drill-down (group → walls).
function buildClientGroups(boq) {
  const map = new Map();
  for (const l of boq) {
    const g = l.wall_group || "";
    if (!map.has(g)) map.set(g, []);
    map.get(g).push(l);
  }
  const entries = [...map.entries()].sort((a, b) =>
    a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0]));
  return entries.map(([name, walls]) => ({
    key: name || "__ungrouped__",
    name,
    walls: [...walls].sort((a, b) => rankLine(a) - rankLine(b)),
    awaiting: walls.filter((l) => needsClientAction(l)).length,
  }));
}

// ──────────────────────────────────────────────────────────────────
// Portal LIST PAGE — `/my-projects`
// ──────────────────────────────────────────────────────────────────
export default function ClientPortal() {
  const [projects, setProjects] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/my-projects").then((r) => setProjects(r.data))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"));
  }, []);

  if (!projects) return <PortalShell><PortalSkeleton /></PortalShell>;

  // Only count "pending" lines that actually have a design uploaded — brewing
  // items are NOT awaiting the client.
  const totalPending = projects.reduce((a, p) => a + (p.awaiting_your_action ?? p.pending_approval ?? 0), 0);
  // Msg 887 — PSM site-correction notes the client still needs to action.
  const totalSiteNotes = projects.reduce((a, p) => a + (p.site_notes_pending ?? 0), 0);

  return (
    <PortalShell>
      {/* Welcome hero — warm brand wash matching the MIPL logo backdrop */}
      <div className="px-6 py-10 sm:px-10 sm:py-16 bg-brand-hero border-b border-orange-100" data-testid="portal-hero">
        <div className="max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 backdrop-blur border border-white text-[11px] font-medium text-orange-700 mb-5 shadow-sm tracking-wide">
            <Sun className="w-3.5 h-3.5 text-amber-500" />
            Welcome, {user?.name || "client"}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-zinc-900 font-medium max-w-3xl">
            Your projects, brought to life with&nbsp;
            <em className="text-orange-700 not-italic">care, craft and clarity</em>.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-700 max-w-2xl leading-relaxed">
            Review designs, approve revisions and follow every project from
            concept through installation — all in one place.
          </p>

          {totalPending > 0 ? (
            <div className="mt-7 inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/90 border border-amber-200 shadow-sm"
                 data-testid="portal-pending-strip">
              <Bell className="w-5 h-5 text-amber-600" />
              <div className="text-sm">
                <div className="font-semibold text-amber-900">
                  {totalPending} {totalPending === 1 ? "design is" : "designs are"} waiting for your approval
                </div>
                <div className="text-xs text-amber-700">Scroll down to see the items highlighted below.</div>
              </div>
            </div>
          ) : projects.length > 0 ? (
            <div className="mt-7 inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/90 border border-orange-200 shadow-sm"
                 data-testid="portal-brewing-strip">
              <Palette className="w-5 h-5 text-orange-600" />
              <div className="text-sm">
                <div className="font-semibold text-orange-900">
                  Our designers are brewing up something beautiful for you ☕
                </div>
                <div className="text-xs text-orange-700">We'll ping you the moment there's a design to review.</div>
              </div>
            </div>
          ) : null}

          {totalSiteNotes > 0 && (
            <div className="mt-4 inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/90 border border-blue-200 shadow-sm"
                 data-testid="portal-sitenote-strip">
              <PencilRuler className="w-5 h-5 text-blue-600" />
              <div className="text-sm">
                <div className="font-semibold text-blue-900">
                  {totalSiteNotes} site {totalSiteNotes === 1 ? "correction needs" : "corrections need"} your action
                </div>
                <div className="text-xs text-blue-700">Our team left notes from the site visit — open a project to action &amp; confirm.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Project grid */}
      <div className="px-6 sm:px-10 py-10">
        <div className="max-w-5xl mx-auto">
          {projects.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Action-Required section pinned to top */}
              {(totalPending > 0 || totalSiteNotes > 0) && (
                <div className="mb-10" data-testid="portal-action-required">
                  <h2 className="text-xs sm:text-sm font-mono uppercase tracking-widest text-amber-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Action Required From You
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {projects
                      .filter((p) => (p.awaiting_your_action ?? 0) > 0 || (p.site_notes_pending ?? 0) > 0)
                      .map((p) => (
                        <ProjectCard key={p.project_code} p={p} highlight />
                      ))}
                  </div>
                </div>
              )}

              <h2 className="text-xs sm:text-sm font-mono uppercase tracking-widest text-zinc-500 mb-3">
                All Projects
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {projects.map((p) => (
                  <ProjectCard key={p.project_code} p={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </PortalShell>
  );
}

function ProjectCard({ p, highlight }) {
  const pct = p.progress_pct || 0;
  const pctCls = pct >= 80 ? "bg-emerald-500"
    : pct >= 40 ? "bg-orange-500"
    : "bg-amber-500";
  return (
    <Link to={`/my-projects/${p.project_code}`}
          className={`block rounded-2xl border p-5 bg-white hover:shadow-lg transition-all
            ${highlight ? "border-amber-300 ring-2 ring-amber-100 bg-gradient-to-br from-amber-50/40 to-white"
                        : "border-zinc-200 hover:border-orange-200"}`}
          data-testid={`portal-project-${p.project_code}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] text-orange-600 font-medium mb-1"><ProjRef code={p.project_code} /></div>
          <div className="font-display text-lg font-bold tracking-tight text-zinc-900 truncate">{p.name}</div>
          {p.location && <div className="text-xs text-zinc-500 mt-0.5 truncate">{p.location}</div>}
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-1" />
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Overall Progress</span>
          <span className="text-sm font-bold text-zinc-900">{pct}%</span>
        </div>
        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
          <div className={`h-full ${pctCls} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Mini stats */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <MiniStat label="Items" value={p.boq_total} tone="zinc" />
        <MiniStat label="Awaiting" value={p.awaiting_your_action ?? 0} tone={(p.awaiting_your_action ?? 0) ? "amber" : "zinc"} />
        <MiniStat label="Approved" value={p.approved} tone="emerald" />
        <MiniStat label="Done" value={p.completed} tone="green" />
      </div>

      {(p.site_notes_pending ?? 0) > 0 && (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5"
             data-testid={`portal-sitenote-badge-${p.project_code}`}>
          <PencilRuler className="w-3.5 h-3.5 flex-shrink-0" />
          {p.site_notes_pending} site {p.site_notes_pending === 1 ? "correction needs" : "corrections need"} your action
        </div>
      )}
    </Link>
  );
}

function MiniStat({ label, value, tone }) {
  const bg = {
    zinc: "bg-zinc-50 text-zinc-700",
    amber: "bg-amber-50 text-amber-800",
    emerald: "bg-emerald-50 text-emerald-800",
    green: "bg-green-50 text-green-900",
  }[tone] || "bg-zinc-50 text-zinc-700";
  return (
    <div className={`rounded-lg py-2 ${bg}`}>
      <div className="font-display font-bold text-base leading-none">{value || 0}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest mt-0.5 opacity-70">{label}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-24 text-center" data-testid="portal-empty">
      <div className="inline-block p-5 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 mb-4">
        <Sparkles className="w-8 h-8 text-orange-600" />
      </div>
      <h3 className="font-display text-2xl font-bold mb-2">No projects yet</h3>
      <p className="text-zinc-500 max-w-md mx-auto">
        As soon as a project is created for you, it will appear here and we'll guide you through each step.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Portal DETAIL PAGE — `/my-projects/:code`
// ──────────────────────────────────────────────────────────────────
export function ClientProjectDetail() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [review, setReview] = useState(null);  // BOQ line being reviewed
  const [gkey, setGkey] = useState(null);       // selected sub-scope group

  function load() {
    api.get(`/my-projects/${code}`).then((r) => setData(r.data))
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail) || "Failed"));
    // Phase 34 (Msg 738) — strictly install photos only (no site-measurement,
    // no signed DC). Endpoint sources from challan-line installation_photo_url.
    api.get(`/my-projects/${code}/install-gallery`).then((r) => setPhotos(r.data || []))
      .catch(() => setPhotos([]));
  }
  useEffect(load, [code]);

  const boq = data?.boq || [];
  const [view, setView] = useState("overview");
  const [fp, setFp] = useState(null);
  useEffect(() => {
    api.get(`/my-projects/${code}/floorplan`)
      .then((r) => setFp(r.data)).catch(() => setFp(null));
  }, [code]);
  const counts = useMemo(() => ({
    all: boq.length,
    awaiting: boq.filter((l) => needsClientAction(l)).length,
    approved: boq.filter((l) => ["completed", "done"].includes(l.client_design_approval)).length,
    modify: boq.filter((l) => l.client_design_approval === "modify").length,
    in_production: boq.filter((l) => deriveStage(l) === "in_production").length,
    completed: boq.filter((l) => deriveStage(l) === "completed").length,
  }), [boq]);

  // Msg 623 — sub-scope groups for the drill-down. When there is only one
  // (ungrouped) bucket we fall back to a flat single list.
  const groups = useMemo(() => buildClientGroups(boq), [boq]);
  const noGroups = groups.length <= 1 && groups[0]?.name === "";
  const activeGroup = useMemo(() => {
    if (noGroups) return groups[0];
    return groups.find((g) => g.key === gkey)
      || groups.find((g) => g.awaiting > 0)
      || groups[0];
  }, [groups, gkey, noGroups]);

  if (!data) return <PortalShell><PortalSkeleton /></PortalShell>;
  const { project } = data;

  return (
    <PortalShell>
      {/* Detail hero */}
      <div className="px-6 py-8 sm:px-10 sm:py-10 bg-brand-hero border-b border-orange-100">
        <div className="max-w-5xl mx-auto">
          <Link to="/my-projects"
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-orange-700 mb-4 group"
                data-testid="portal-back">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> All projects
          </Link>
          <div className="font-mono text-xs text-orange-600 font-medium"><ProjRef code={project.project_code} /></div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900 mt-1">
            {project.name}
          </h1>
          {project.location && <div className="text-zinc-600 mt-1">{project.location}</div>}

          {/* Big progress bar */}
          <div className="mt-6 bg-white/80 backdrop-blur rounded-2xl border border-white shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Overall Progress</div>
                <div className="font-display text-4xl font-bold text-zinc-900">{project.progress_pct}%</div>
              </div>
              <TrendingUp className="w-10 h-10 text-orange-400" />
            </div>
            <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-500 via-amber-400 to-red-400 transition-all"
                   style={{ width: `${project.progress_pct}%` }} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
              <StatPill label="Items" v={counts.all} icon={Layers} tone="zinc" />
              <StatPill label="Awaiting You" v={counts.awaiting} icon={Eye} tone="amber" />
              <StatPill label="Approved" v={counts.approved} icon={CheckCircle2} tone="emerald" />
              <StatPill label="In Production" v={counts.in_production} icon={Rocket} tone="orange" />
              <StatPill label="Completed" v={counts.completed} icon={Trophy} tone="green" />
            </div>
          </div>
        </div>
      </div>

      {/* View switcher + client-safe interactive floorplan */}
      {fp?.floorplan?.image_url && <ClientViewTabs view={view} onChange={setView} />}
      {view === "floorplan" && fp?.floorplan?.image_url && (
        <ClientFloorplan code={code} data={fp} />
      )}

      {/* BOQ cards grouped by sub-scope — a left rail lists the groups
          (e.g. "Reception walls · 4"); selecting one opens its walls on the
          right for review/approval. Mirrors the Designer/Pre-Audit drill-down
          so large projects (200+ walls) stay easy to navigate. (Msg 623) */}
      <div className={`px-6 sm:px-10 py-8 ${view !== "overview" ? "hidden" : ""}`}>
        <div className="max-w-5xl mx-auto">
          {boq.length === 0 ? (
            <div className="py-20 text-center text-stone-400" data-testid="portal-boq-empty">
              Your project is being set up. Check back in a moment.
            </div>
          ) : noGroups ? (
            <div className="space-y-4">
              {(activeGroup?.walls || []).map((line) => (
                <BoqCard key={line.id} line={line} code={code}
                         onReview={() => setReview(line)} onChange={load} />
              ))}
            </div>
          ) : (
            <div className="grid lg:grid-cols-[minmax(240px,300px)_1fr] gap-6 items-start">
              <ClientGroupRail groups={groups} active={activeGroup?.key} onSelect={setGkey} />
              <div className="space-y-4" data-testid={`portal-group-walls-${activeGroup?.key}`}>
                <div className="flex items-center gap-2 px-1">
                  <Layers className="w-4 h-4 text-orange-600" />
                  <h2 className="font-display text-lg font-bold tracking-tight text-zinc-900">
                    {activeGroup?.name || "Other items"}
                  </h2>
                  <span className="text-xs text-zinc-400">
                    · {activeGroup?.walls.length} item{activeGroup?.walls.length !== 1 ? "s" : ""}
                  </span>
                </div>
                {(activeGroup?.walls || []).map((line) => (
                  <BoqCard key={line.id} line={line} code={code}
                           onReview={() => setReview(line)} onChange={load} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Installation Gallery — STRICT: install photos only (no site
          measurement, no signed DC). View-only — right-click + drag disabled.
          Source: /api/my-projects/{code}/install-gallery */}
      {view === "overview" && photos.length > 0 && (
        <div className="px-6 sm:px-10 pb-12" data-testid="portal-gallery">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
                <span className="inline-flex w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white items-center justify-center">
                  <Camera className="w-4 h-4" />
                </span>
                Installation Gallery
              </h3>
              <span className="text-xs font-mono text-zinc-500">
                {photos.length} photo{photos.length === 1 ? "" : "s"} · view-only
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((ph) => (
                <div key={ph.id || ph.file_url}
                     onContextMenu={(e) => e.preventDefault()}
                     className="group relative rounded-xl overflow-hidden border border-zinc-200 bg-black select-none"
                     data-testid={`portal-gallery-${ph.id || ""}`}>
                  <img src={ph.file_url}
                       alt={ph.material_name || "installed"}
                       draggable={false}
                       onContextMenu={(e) => e.preventDefault()}
                       onDragStart={(e) => e.preventDefault()}
                       className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none"
                       onError={(e) => { e.target.style.display = "none"; }} />
                  {/* Watermark overlay — confidential / view-only */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="rotate-[-18deg] text-white/30 text-[11px] font-mono tracking-[0.4em] whitespace-nowrap">
                      MIPL · INSTALLED · VIEW ONLY
                    </span>
                  </div>
                  {(ph.material_name || ph.wall_no || ph.wall_name) && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent px-3 py-2 text-white text-xs">
                      {(ph.wall_name || "").trim() && (ph.wall_no || "").trim() && (
                        <span className="font-mono text-amber-300 mr-1">{ph.wall_no}</span>
                      )}
                      {wallLabel(ph)}
                    </div>
                  )}
                  {ph.installed_at && (
                    <div className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-wider bg-emerald-600/90 text-white px-2 py-0.5 rounded">
                      {(ph.installed_at || "").slice(0, 10)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {review && (
        <DesignReviewModal projectCode={code} line={review}
                           onClose={() => setReview(null)}
                           onSaved={() => { setReview(null); load(); }} />
      )}
    </PortalShell>
  );
}

// Msg 623 — left rail of sub-scope groups for the client drill-down.
function ClientGroupRail({ groups, active, onSelect }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl divide-y divide-zinc-100 overflow-hidden lg:sticky lg:top-20"
         data-testid="portal-group-rail">
      {groups.map((g) => (
        <button key={g.key} onClick={() => onSelect(g.key)}
                data-testid={`portal-group-row-${g.key}`}
                className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors
                  ${g.key === active ? "bg-orange-50/70" : "hover:bg-zinc-50"}`}>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
            ${g.name ? "bg-orange-50 text-orange-600" : "bg-zinc-100 text-zinc-400"}`}>
            <Layers className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-zinc-900 truncate">
              {g.name || "Other items"}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {g.walls.length} item{g.walls.length !== 1 ? "s" : ""}
              {g.awaiting > 0 && <span className="text-amber-600"> · {g.awaiting} awaiting you</span>}
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${g.key === active ? "text-orange-500" : "text-zinc-300"}`} />
        </button>
      ))}
    </div>
  );
}

function StatPill({ label, v, icon: Icon, tone }) {
  const cls = {
    zinc: "bg-zinc-50 text-zinc-700",
    amber: "bg-amber-50 text-amber-800",
    emerald: "bg-emerald-50 text-emerald-800",
    indigo: "bg-indigo-50 text-indigo-800",
    orange: "bg-orange-50 text-orange-800",
    green: "bg-green-50 text-green-900",
  }[tone] || "bg-zinc-50 text-zinc-700";
  return (
    <div className={`${cls} rounded-lg px-3 py-2 flex items-center gap-2`}>
      <Icon className="w-4 h-4 opacity-60" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm leading-none">{v || 0}</div>
        <div className="text-[10px] opacity-70 truncate">{label}</div>
      </div>
    </div>
  );
}

function BoqCard({ line, code, onReview, onChange }) {
  const stage = deriveStage(line);
  const chip = STAGE_CHIP[stage] || STAGE_CHIP.brewing;
  const ChipIcon = chip.icon;
  const actionable = stage === "pending_approval";
  const siteNoteOpen = !!line.site_client_note && line.site_client_note_status !== "completed";
  const prodFileOpen = !!line.production_file_url && (line.production_file_status || "pending") === "pending";
  const [open, setOpen] = useState(actionable || siteNoteOpen || prodFileOpen);
  const thumb = line.current_design_file?.file_url || line.approved_design_file?.file_url
                || line.artwork_image_url || "";
  const thumbIsImage = isImage(thumb);

  // 5-milestone client pipeline
  const has = (v) => v === "completed" || v === "done";
  const isActive = (v) => v === "in_progress";
  const outsourced = line.production_type === "outsourced";
  const designState = has(line.client_design_approval) ? "done"
    : line.client_design_approval === "modify" || line.client_design_approval === "rejected" ? "blocked"
    : actionable ? "action"
    : line.current_design_file ? "active" : "pending";
  const prodfileState = line.production_file_status === "approved" ? "done"
    : line.production_file_status === "modify" || line.production_file_status === "rejected" ? "blocked"
    : line.production_file_url ? "action"
    : has(line.client_design_approval) ? "pending" : "locked";
  const milestones = outsourced ? [
    // Outsourced: shorter journey — Design → Site Measurements → Final
    // Production File → Completed. Production / Delivery / Installation are
    // NOT shown.
    { key: "design", label: "Design", state: designState },
    {
      key: "site", label: "Site Measurements",
      state: has(line.site_audit) ? "done"
           : isActive(line.site_audit) ? "active"
           : has(line.client_design_approval) ? "pending" : "locked",
    },
    { key: "prodfile", label: "Final Production File", state: prodfileState },
    {
      key: "complete", label: "Completed",
      state: line.production_file_status === "approved" ? "done" : "locked",
    },
  ] : [
    {
      key: "design", label: "Design",
      state: designState,
    },
    {
      key: "site", label: "Site Measurements",
      state: has(line.site_audit) ? "done"
           : isActive(line.site_audit) ? "active"
           : has(line.client_design_approval) ? "pending" : "locked",
    },
    {
      key: "prodfile", label: "Production File Approval",
      state: line.production_file_status === "approved" ? "done"
           : line.production_file_status === "modify" || line.production_file_status === "rejected" ? "blocked"
           : line.production_file_url ? "action"      // your turn to review
           : has(line.site_audit) ? "pending" : "locked",
    },
    {
      key: "production", label: "Production",
      state: has(line.production_progress) ? "done"
           : isActive(line.production_progress) ? "active"
           : line.production_file_status === "approved" ? "pending" : "locked",
    },
    {
      key: "delivery", label: "Delivery",
      state: has(line.delivery_progress) ? "done"
           : isActive(line.delivery_progress) ? "active"
           : has(line.production_progress) ? "pending" : "locked",
    },
    {
      key: "install", label: "Installation",
      state: has(line.installation_progress) ? "done"
           : isActive(line.installation_progress) ? "active"
           : has(line.delivery_progress) ? "pending" : "locked",
    },
  ];
  const rejHist = line.rejection_history || [];

  return (
    <div className={`rounded-2xl border bg-white transition-all hover:shadow-md
      ${actionable ? "border-amber-300 ring-2 ring-amber-100" : "border-zinc-200"}`}
      data-testid={`portal-boq-${line.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`portal-boq-toggle-${line.id}`}
              className="w-full flex items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-zinc-50/60 transition-colors">
        <div className="min-w-0 flex items-center gap-3">
          <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          <div className="min-w-0">
            {(line.wall_name || "").trim() && (line.wall_no || "").trim() && (
              <div className="font-mono text-[11px] text-orange-600 font-medium">{line.wall_no}</div>
            )}
            <h3 className="font-display text-base sm:text-lg font-bold tracking-tight text-zinc-900 truncate">
              {wallLabel(line)}
            </h3>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${chip.chip}`}
              data-testid={`portal-boq-stage-${line.id}`}>
          <ChipIcon className="w-3.5 h-3.5" />
          {line.production_type === "outsourced" && stage === "completed" ? "Completed 🎉" : chip.label}
        </span>
      </button>

      {open && (
      <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-5 flex flex-col sm:flex-row gap-5 border-t border-zinc-100">
        {/* Thumbnail */}
        <div className="flex-shrink-0">
          <div className="w-full sm:w-32 h-32 rounded-xl overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-50 border border-zinc-200 flex items-center justify-center">
            {thumbIsImage ? (
              <img src={thumb} alt={line.material_name} className="w-full h-full object-cover"
                   onError={(e) => { e.target.style.display = "none"; }} />
            ) : thumb ? (
              <FileText className="w-8 h-8 text-zinc-400" />
            ) : (
              <Palette className="w-8 h-8 text-zinc-300" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {line.hsn_description && (
            <div className="text-xs text-zinc-500 mb-1">{line.hsn_description}</div>
          )}

          {/* Status facts — strictly NO measurements/quantities for clients.
              Only the design rejection/revision indicator is surfaced here. */}
          {rejHist.length > 0 && (
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] border border-rose-200"
                    title={`V${rejHist[rejHist.length - 1].version_number} rejected — designer working on V${(rejHist[rejHist.length - 1].version_number || 0) + 1}`}>
                ↺ V{rejHist[rejHist.length - 1].version_number} rejected → V{(rejHist[rejHist.length - 1].version_number || 0) + 1} {line.current_design_file ? "pending review" : "in progress"}
              </span>
            </div>
          )}

          {/* 5-Milestone pipeline — visual journey */}
          <div className="mt-4 flex items-center gap-1 sm:gap-2" data-testid={`portal-pipeline-${line.id}`}>
            {milestones.map((m, i) => (
              <MilestoneChip key={m.key} m={m} isLast={i === milestones.length - 1} />
            ))}
          </div>

          {line.current_design_file?.client_comment && actionable && (
            <div className="mt-3 text-sm text-zinc-600 bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1">Note from the team</div>
              "{line.current_design_file.client_comment}"
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {line.current_design_file ? (
              <button onClick={onReview}
                      className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-all
                        ${actionable
                          ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5"
                          : "bg-zinc-900 text-white hover:bg-zinc-800"}`}
                      data-testid={`portal-review-${line.id}`}>
                <Eye className="w-4 h-4" />
                {actionable ? "Review & Approve" : "View Design"}
              </button>
            ) : (
              <div className="text-xs text-orange-600 italic font-medium inline-flex items-center gap-1.5">
                <Palette className="w-4 h-4" /> Our design team is crafting this piece for you.
              </div>
            )}
          </div>

          {/* Production file review (Phase 30) — view-only, no download */}
          {line.production_file_url && (
            <ProductionFileReview line={line} code={code} onChange={onChange} />
          )}

          {/* Msg 890 — site visit note: a subordinate inset aligned to THIS line. */}
          {line.site_client_note && (
            <SiteVisitNote line={line} code={code} onChange={onChange} />
          )}
        </div>
      </div>
      )}
    </div>
  );
}

// Msg 889/890 — Site visit note as a neat, subordinate inset inside the line's
// own card (bg-stone-50), with a site-photo thumbnail strip + lightbox. Stays
// visually below the dominant amber "Review & Approve" CTA, so it never blocks
// approval, and is always aligned to its line (no cluttered global list).
function SiteVisitNote({ line, code, onChange }) {
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const done = line.site_client_note_status === "completed";
  // Show ONLY the photo the PSM chose to attach (Msg 906 — "with or without the photo").
  const photos = (line.site_client_note_photo ? [line.site_client_note_photo] : []).filter(Boolean);

  async function markDone() {
    setBusy(true);
    try {
      await api.post(`/my-projects/${code}/boq/${line.id}/site-note/acknowledge`,
                     { response: response.trim() });
      toast.success("Thanks! We've let our team know the site is ready.");
      onChange && onChange();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't update — please retry");
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-4 rounded-xl bg-stone-50 border border-stone-100 p-4"
         data-testid={`portal-sitenote-${line.id}`}>
      <div className="flex items-center gap-2.5 mb-2.5">
        <div className="w-7 h-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
          <PencilRuler className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">
            Site visit note
          </div>
          {line.site_client_note_by && (
            <div className="text-[11px] text-stone-400 truncate">from {line.site_client_note_by}, our site team</div>
          )}
        </div>
        <span className={`text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0 ${done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {done ? "Done ✓" : "Action needed"}
        </span>
      </div>

      <p className="text-sm text-stone-700 leading-relaxed">{line.site_client_note}</p>

      {photos.length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid={`portal-sitephotos-${line.id}`}>
          {photos.map((url, i) => (
            <button key={i} type="button" onClick={() => setLightbox(url)}
                    data-testid={`site-photo-thumbnail-${line.id}-${i}`}
                    className="w-14 h-14 rounded-md overflow-hidden border border-stone-200 flex-shrink-0 hover:ring-2 hover:ring-amber-500 hover:ring-offset-2 hover:ring-offset-stone-50 transition-all">
              <img src={url} alt={`Site photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      {done ? (
        <div className="mt-3 text-[11px] text-emerald-700 flex items-start gap-1.5"
             data-testid={`portal-sitenote-done-${line.id}`}>
          <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
          <span>Confirmed by you{line.site_client_note_response ? ` — "${line.site_client_note_response}"` : ""}</span>
        </div>
      ) : (
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
          <input value={response} onChange={(e) => setResponse(e.target.value)}
                 placeholder="Optional reply (e.g. done — area cleared)"
                 data-testid={`portal-sitenote-response-${line.id}`}
                 className="flex-1 bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all" />
          <button onClick={markDone} disabled={busy}
                  data-testid={`portal-sitenote-done-btn-${line.id}`}
                  className="inline-flex items-center justify-center gap-1.5 px-4 h-10 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 active:scale-[0.98] transition-all flex-shrink-0">
            <CheckCircle2 className="w-4 h-4" /> {busy ? "Saving…" : "Mark as done"}
          </button>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-stone-950/80 backdrop-blur-sm flex items-center justify-center p-6"
             onClick={() => setLightbox(null)} data-testid="site-photo-lightbox">
          <img src={lightbox} alt="Site photo" className="max-w-full max-h-full rounded-xl shadow-2xl" />
          <button onClick={() => setLightbox(null)}
                  className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}

// Production file review for clients (Phase 30) — view-only, no download.
function ProductionFileReview({ line, code, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showComment, setShowComment] = useState(null); // 'modify' | 'reject'
  const [comment, setComment] = useState("");
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbErr, setThumbErr] = useState(false);
  const [thumbType, setThumbType] = useState("");
  const status = line.production_file_status || "pending";

  // Phase 34 (Msg 738 round 3): auto-load a low-res inline thumbnail so the
  // client SEES the artwork the moment they land on the page — no extra
  // click required. The "View file" button still opens the larger preview.
  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    async function load() {
      try {
        const tok = localStorage.getItem("token");
        const r = await fetch(
          `${(api.defaults.baseURL || "")}/projects/${code}/boq/${line.id}/production-file/view?preview=1`,
          { headers: { Authorization: `Bearer ${tok}` } },
        );
        if (!r.ok) throw new Error(String(r.status));
        const blob = await r.blob();
        if (cancelled) return;
        revoke = URL.createObjectURL(blob);
        setThumbUrl(revoke);
        setThumbType(blob.type || "");
      } catch (e) {
        if (!cancelled) setThumbErr(true);
      }
    }
    if (line.production_file_url) load();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [code, line.id, line.production_file_url, line.production_file_uploaded_at]);

  async function viewFile() {
    setBusy(true);
    try {
      const tok = localStorage.getItem("token");
      // Phase 34 (Msg 738 round 3): always request the low-res preview for
      // client viewing — print files can be 50–200 MB (300dpi @ A1) which
      // fail to render inline. The preview endpoint serves a 1600px JPEG
      // generated server-side (cached after first request).
      const r = await fetch(
        `${(api.defaults.baseURL || "")}/projects/${code}/boq/${line.id}/production-file/view?preview=1`,
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open("", "_blank", "noopener,noreferrer");
      if (w) {
        w.document.write(
          `<html><head><title>Production File · view-only</title>
           <style>body{margin:0;background:#1a1a1a;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:system-ui;color:#fff}
           .wrap{max-width:100%;max-height:100vh;text-align:center;padding:24px}
           img,iframe{max-width:100vw;max-height:100vh;display:block;margin:0 auto;box-shadow:0 8px 40px rgba(0,0,0,.5);border-radius:6px}
           .lock{position:fixed;top:14px;left:14px;font-size:11px;background:rgba(255,255,255,.1);padding:6px 12px;border-radius:99px;font-family:monospace;letter-spacing:.1em;z-index:10}
           .hint{position:fixed;bottom:14px;right:14px;font-size:11px;background:rgba(255,255,255,.08);padding:6px 12px;border-radius:99px;color:#aaa;letter-spacing:.04em;z-index:10}
           </style></head>
           <body oncontextmenu="return false" onkeydown="if((event.ctrlKey||event.metaKey)&&['s','p'].includes(event.key.toLowerCase())){event.preventDefault();return false;}">
           <div class="lock">VIEW-ONLY · MIPL CONFIDENTIAL</div>
           <div class="hint">${blob.type === "application/pdf" ? "Original PDF" : "Low-res preview · final print is full-resolution"}</div>
           <div class="wrap">${blob.type === "application/pdf"
             ? `<iframe src="${url}#toolbar=0&navpanes=0" style="width:100vw;height:100vh;border:0"></iframe>`
             : `<img src="${url}" alt="Production file" draggable="false" />`}</div>
           </body></html>`);
        w.document.close();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message === "403" ? "Not authorised" : "Couldn't load preview — please refresh and try again");
    } finally {
      setBusy(false);
    }
  }

  async function act(action) {
    if (action !== "approve" && !comment.trim()) {
      toast.error("Please write a short comment");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/projects/${code}/boq/${line.id}/production-file/${action}`,
        { comment: action === "approve" ? "" : comment.trim() },
      );
      toast.success(
        action === "approve" ? "Approved — production team has been notified"
        : action === "modify" ? "Sent back for revisions"
        : "Rejected"
      );
      setShowComment(null);
      setComment("");
      onChange && onChange();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  }

  // Already approved — read-only confirmation
  if (status === "approved") {
    return (
      <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"
           data-testid={`prodfile-approved-${line.id}`}>
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle2 className="w-4 h-4" />
          <div className="font-medium text-sm">Production file approved · Production has started</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50 p-4"
         data-testid={`prodfile-review-${line.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-display text-sm font-bold text-zinc-900">
            Production file ready for your review
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            View-only · downloads are disabled to keep your artwork safe.
          </div>
        </div>
        {status !== "pending" && (
          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-mono uppercase border bg-amber-100 text-amber-800 border-amber-300">
            {status === "modify" ? "Awaiting revisions" : "Rejected"}
          </span>
        )}
      </div>

      {/* Inline preview thumbnail — auto-loads (low-res, view-only) */}
      <div className="mb-3 rounded-xl overflow-hidden border border-amber-200 bg-zinc-900 select-none relative"
           onContextMenu={(e) => e.preventDefault()}
           data-testid={`prodfile-thumb-${line.id}`}>
        {thumbUrl ? (
          thumbType === "application/pdf" ? (
            <iframe src={`${thumbUrl}#toolbar=0&navpanes=0`}
                    title="Production file preview"
                    className="w-full h-[420px] sm:h-[480px] bg-white pointer-events-auto" />
          ) : (
            <img src={thumbUrl} alt="Production file preview"
                 draggable={false}
                 onDragStart={(e) => e.preventDefault()}
                 className="w-full max-h-[480px] object-contain pointer-events-none" />
          )
        ) : thumbErr ? (
          <div className="p-8 text-center text-amber-200 text-sm">
            Couldn&apos;t generate a preview for this file format. Use the
            &ldquo;View file&rdquo; button below to open it.
          </div>
        ) : (
          <div className="p-12 flex items-center justify-center text-zinc-400 text-sm gap-2">
            <Clock className="w-4 h-4 animate-pulse" />
            Loading preview…
          </div>
        )}
        <span className="absolute top-2 left-2 text-[10px] font-mono tracking-[0.18em] bg-black/55 text-white px-2 py-0.5 rounded-full">
          VIEW-ONLY · MIPL CONFIDENTIAL
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={viewFile} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800"
                data-testid={`prodfile-view-${line.id}`}>
          <Eye className="w-4 h-4" /> View file
          <Lock className="w-3 h-3 ml-1 opacity-60" />
        </button>
        {status === "pending" && (
          <>
            <button onClick={() => act("approve")} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                    data-testid={`prodfile-approve-${line.id}`}>
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
            <button onClick={() => setShowComment("modify")} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600"
                    data-testid={`prodfile-modify-${line.id}`}>
              <AlertTriangle className="w-4 h-4" /> Request changes
            </button>
            <button onClick={() => setShowComment("reject")} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-rose-600 text-white text-sm font-medium hover:bg-rose-700"
                    data-testid={`prodfile-reject-${line.id}`}>
              <XCircle className="w-4 h-4" /> Reject
            </button>
          </>
        )}
      </div>

      {showComment && (
        <div className="mt-3">
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
                    placeholder={showComment === "modify"
                      ? "What would you like changed?"
                      : "Tell us why you're rejecting this — we'll start a fresh concept."}
                    data-testid="prodfile-comment"
                    className="w-full px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
          <div className="flex gap-2 mt-2">
            <button onClick={() => act(showComment)} disabled={busy}
                    className="px-4 h-9 rounded-full bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800">Submit</button>
            <button onClick={() => { setShowComment(null); setComment(""); }}
                    className="px-4 h-9 rounded-full border border-zinc-300 text-xs font-medium hover:bg-zinc-50">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// A single milestone in the 5-step client pipeline.
function MilestoneChip({ m, isLast }) {
  const tones = {
    done:    { dot: "bg-emerald-500 text-white", line: "bg-emerald-400", txt: "text-emerald-700 font-semibold", icon: CheckCircle2 },
    action:  { dot: "bg-gradient-to-br from-amber-400 to-orange-500 text-white animate-pulse", line: "bg-amber-200", txt: "text-amber-700 font-semibold", icon: Eye },
    active:  { dot: "bg-indigo-500 text-white", line: "bg-indigo-200", txt: "text-indigo-700 font-medium", icon: Rocket },
    pending: { dot: "bg-white border-2 border-zinc-300 text-zinc-400", line: "bg-zinc-200", txt: "text-zinc-500", icon: Clock },
    locked:  { dot: "bg-zinc-100 border border-zinc-200 text-zinc-300", line: "bg-zinc-100", txt: "text-zinc-400", icon: Clock },
    blocked: { dot: "bg-rose-500 text-white", line: "bg-rose-200", txt: "text-rose-700 font-semibold", icon: X },
  }[m.state] || { dot: "bg-zinc-100 text-zinc-300", line: "bg-zinc-100", txt: "text-zinc-400", icon: Clock };
  const Icon = tones.icon;
  return (
    <div className="flex-1 flex items-center min-w-0" data-testid={`portal-milestone-${m.key}`}>
      <div className="flex flex-col items-center gap-1.5 min-w-[64px] sm:min-w-[84px]">
        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-sm ${tones.dot}`}>
          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </div>
        <div className={`text-[9px] sm:text-[10px] font-mono uppercase tracking-wider text-center leading-tight ${tones.txt}`}>
          {m.label}
        </div>
      </div>
      {!isLast && <div className={`h-0.5 flex-1 ${tones.line} mx-0.5 sm:mx-1 mt-[-18px]`} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Design Review Modal — approve OR request changes
// ──────────────────────────────────────────────────────────────────
function DesignReviewModal({ projectCode, line, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ versions: [], comments: [] });
  const [mode, setMode] = useState(null);            // null | 'approve' | 'modify' | 'reject'
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectCode}/boq/${line.id}/design-files`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); setLoading(false); });
  }, [projectCode, line.id]);

  const latest = data.versions[0];      // current version (desc order)
  const approvedVersion = data.versions.find((v) => v.is_approved_version);

  async function approve() {
    if (!latest) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectCode}/boq/${line.id}/design-files/${latest.id}/approve`);
      toast.success("Design approved! This item will now move to production.");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  async function requestMod() {
    if (!comment.trim()) { toast.error("Please describe the change you'd like"); return; }
    if (!latest) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectCode}/boq/${line.id}/design-files/${latest.id}/request-modification`,
                     { comment: comment.trim() });
      toast.success("Your modification request has been sent to the project team.");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  async function rejectDesign() {
    if (!comment.trim()) { toast.error("Please share a reason for rejection"); return; }
    if (!latest) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectCode}/boq/${line.id}/design-files/${latest.id}/reject`,
                     { comment: comment.trim() });
      toast.success("Design declined. Your feedback has been sent to the project team.");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
         data-testid="portal-review-modal">
      <div className="bg-white w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[92vh] sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between bg-white sticky top-0 z-10">
          <div className="min-w-0">
            {(line.wall_name || "").trim() && (line.wall_no || "").trim() && (
              <div className="font-mono text-[11px] text-orange-600 font-medium">{line.wall_no}</div>
            )}
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 truncate">
              {wallLabel(line)}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full"
                  data-testid="portal-review-close">
            <X className="w-5 h-5 text-zinc-600" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-zinc-400">Loading designs…</div>
          ) : data.versions.length === 0 ? (
            <div className="p-10 text-center text-zinc-400">No design shared yet.</div>
          ) : (
            <div className="grid md:grid-cols-[1.3fr_1fr]">
              {/* Left: preview */}
              <div className="bg-gradient-to-br from-zinc-50 to-white p-6 md:p-8 min-h-[60vh] flex items-center justify-center border-r border-zinc-100"
                   data-testid="portal-review-preview">
                <PreviewPanel file={latest} />
              </div>
              {/* Right: version timeline + actions */}
              <div className="p-5 sm:p-6 space-y-5">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Current Version</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 text-xs font-bold">
                      V{latest.version_number}
                    </span>
                    <div className="text-sm text-zinc-700">
                      <div className="font-medium">Latest design</div>
                      <div className="text-xs text-zinc-500">
                        Shared {formatDate(latest.uploaded_at)}
                      </div>
                    </div>
                  </div>
                  {latest.client_comment && (
                    <div className="mt-3 text-sm text-zinc-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1">Note from the team</div>
                      "{latest.client_comment}"
                    </div>
                  )}
                </div>

                {/* Action buttons — hidden after client has acted; reappear
                    only when the designer uploads a fresh version (status
                    reverts to "pending_client_approval"). Msg 1063. */}
                {mode === null
                  && !latest.is_approved_version
                  && latest.status !== "modification_requested"
                  && latest.status !== "rejected" && (
                  <div className="space-y-2">
                    <button onClick={() => setMode("approve")}
                            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                            data-testid="portal-approve-btn">
                      <CheckCircle2 className="w-5 h-5" /> Approve Design
                    </button>
                    <button onClick={() => setMode("modify")}
                            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-purple-300 text-purple-700 font-medium hover:bg-purple-50 transition-all"
                            data-testid="portal-modify-btn">
                      <MessageSquare className="w-5 h-5" /> Request Changes
                    </button>
                    <button onClick={() => setMode("reject")}
                            className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-rose-300 text-rose-700 font-medium hover:bg-rose-50 transition-all"
                            data-testid="portal-reject-btn">
                      <X className="w-5 h-5" /> Reject
                    </button>
                  </div>
                )}

                {/* Waiting-on-designer banner — appears after client has
                    already requested a change or rejected the latest design. */}
                {mode === null && !latest.is_approved_version
                  && (latest.status === "modification_requested" || latest.status === "rejected") && (
                  <div className={`rounded-xl border-2 p-4 ${latest.status === "rejected" ? "border-rose-200 bg-rose-50" : "border-purple-200 bg-purple-50"}`}
                       data-testid="portal-awaiting-revision">
                    <div className={`font-display text-base font-bold mb-1 ${latest.status === "rejected" ? "text-rose-900" : "text-purple-900"}`}>
                      {latest.status === "rejected" ? "Feedback sent — a fresh concept is on the way" : "Revision requested — the team is on it"}
                    </div>
                    <div className={`text-sm ${latest.status === "rejected" ? "text-rose-800" : "text-purple-800"}`}>
                      Your feedback has reached the project team and a revised version will be shared with you. The
                      Approve / Request changes / Reject options will reappear here as soon
                      as the new draft is ready.
                    </div>
                  </div>
                )}

                {mode === "approve" && (
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4"
                       data-testid="portal-approve-confirm">
                    <div className="font-display text-base font-bold text-emerald-900 mb-2">
                      Confirm approval?
                    </div>
                    <div className="text-sm text-emerald-800 mb-4">
                      Once approved, this version moves to production file preparation and cannot be changed from here.
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setMode(null)}
                              className="flex-1 h-10 rounded-lg border border-zinc-300 text-zinc-700 hover:bg-white">
                        Cancel
                      </button>
                      <button onClick={approve} disabled={saving}
                              className="flex-1 h-10 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
                              data-testid="portal-approve-confirm-btn">
                        {saving ? "Approving…" : "Yes, approve"}
                      </button>
                    </div>
                  </div>
                )}

                {mode === "modify" && (
                  <div className="rounded-xl border-2 border-purple-300 bg-purple-50 p-4"
                       data-testid="portal-modify-form">
                    <div className="font-display text-base font-bold text-purple-900 mb-2">
                      Describe the change
                    </div>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                              placeholder="e.g. Please make the logo bigger and change the background to blue."
                              rows={4}
                              className="w-full rounded-lg border border-purple-200 p-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none bg-white"
                              data-testid="portal-modify-input" />
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setMode(null); setComment(""); }}
                              className="flex-1 h-10 rounded-lg border border-zinc-300 text-zinc-700 hover:bg-white">
                        Cancel
                      </button>
                      <button onClick={requestMod} disabled={saving || !comment.trim()}
                              className="flex-1 h-10 rounded-lg bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50"
                              data-testid="portal-modify-submit">
                        {saving ? "Sending…" : "Send request"}
                      </button>
                    </div>
                  </div>
                )}

                {mode === "reject" && (
                  <div className="rounded-xl border-2 border-rose-300 bg-rose-50 p-4"
                       data-testid="portal-reject-form">
                    <div className="font-display text-base font-bold text-rose-900 mb-2">
                      Reject this design
                    </div>
                    <div className="text-sm text-rose-800 mb-3">
                      The designer will be asked to start over with a fresh concept. Please share what went wrong.
                    </div>
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                              placeholder="e.g. Direction doesn't match our brand — please start with a fresh concept."
                              rows={4}
                              className="w-full rounded-lg border border-rose-200 p-3 text-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none bg-white"
                              data-testid="portal-reject-input" />
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => { setMode(null); setComment(""); }}
                              className="flex-1 h-10 rounded-lg border border-zinc-300 text-zinc-700 hover:bg-white">
                        Cancel
                      </button>
                      <button onClick={rejectDesign} disabled={saving || !comment.trim()}
                              className="flex-1 h-10 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 disabled:opacity-50"
                              data-testid="portal-reject-submit">
                        {saving ? "Sending…" : "Confirm rejection"}
                      </button>
                    </div>
                  </div>
                )}

                {latest.is_approved_version && (
                  <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                    <div>
                      <div className="font-display text-base font-bold text-emerald-900">Design approved</div>
                      <div className="text-xs text-emerald-800 mt-1">
                        Approved on {formatDate(latest.client_action_at)} by {latest.client_action_by}
                      </div>
                    </div>
                  </div>
                )}

                {/* Version history */}
                {data.versions.length > 1 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Version History</div>
                    <div className="space-y-2">
                      {data.versions.slice(1).map((v) => (
                        <VersionRow key={v.id} v={v} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Comments timeline */}
                {data.comments.length > 0 && (
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Conversation</div>
                    <div className="space-y-2">
                      {data.comments.map((c) => (
                        <div key={c.id} className="rounded-lg bg-zinc-50 border border-zinc-100 p-3">
                          <div className="text-xs text-zinc-500 mb-1">
                            {c.by} · {formatDate(c.at)}
                          </div>
                          <div className="text-sm text-zinc-800">"{c.comment}"</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionRow({ v }) {
  const s = v.status === "approved" ? "bg-emerald-100 text-emerald-700"
    : v.status === "modification_requested" ? "bg-purple-100 text-purple-700"
    : "bg-zinc-100 text-zinc-700";
  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-bold">
        V{v.version_number}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">Design version</div>
        <div className="text-[10px] text-zinc-500">{formatDate(v.uploaded_at)}</div>
      </div>
      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${s}`}>
        {v.status.replace(/_/g, " ")}
      </span>
    </div>
  );
}

function PreviewPanel({ file }) {
  if (!file) return null;
  const url = file.file_url;
  const t = (file.file_type || "").toLowerCase();
  const isImg = ["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(t) || isImage(url);
  const isPdf = t === "pdf" || url.toLowerCase().endsWith(".pdf");
  return (
    <div className="w-full">
      {isImg ? (
        <img src={url} alt="design"
             className="max-w-full max-h-[60vh] mx-auto rounded-lg shadow-md"
             onError={(e) => { e.target.style.display = "none"; }} />
      ) : isPdf ? (
        <embed src={url} type="application/pdf" className="w-full h-[60vh] rounded-lg" />
      ) : (
        <div className="text-center py-10">
          <FileText className="w-12 h-12 text-zinc-400 mx-auto mb-3" />
          <div className="font-medium text-zinc-700">Design file</div>
          <div className="text-xs text-zinc-500 mb-4">{t.toUpperCase()} · {formatSize(file.file_size)}</div>
          <a href={url} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800">
            <Download className="w-4 h-4" /> Download to preview
          </a>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Shared shell — minimal top bar (no ERP sidebar for clients)
// ──────────────────────────────────────────────────────────────────
function PortalShell({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-white">
      <header className="h-16 sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-orange-100 flex items-center px-4 sm:px-6">
        <Link to="/my-projects">
          <Logo size={36} showWordmark={true} />
        </Link>
        <span className="text-xs text-orange-500 font-mono uppercase tracking-widest hidden sm:inline ml-3 pl-3 border-l border-orange-200">
          Client Portal
        </span>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <div className="text-xs font-medium text-zinc-800">{user?.name}</div>
            <div className="text-[10px] text-zinc-400">{user?.email}</div>
          </div>
          <button onClick={() => logout().then(() => { window.location.href = "/login"; })}
                  className="text-xs px-3 h-8 rounded-full border border-orange-200 text-orange-700 hover:bg-orange-50 transition-colors"
                  data-testid="portal-logout">
            Sign out
          </button>
        </div>
      </header>
      {children}
      <footer className="py-10 px-6 sm:px-10 text-center">
        <div className="inline-flex items-center gap-2 opacity-60">
          <Logo size={22} showWordmark={false} />
          <span className="text-xs text-zinc-400">
            MIPL GLOBAL · Premium signage &amp; graphics
          </span>
        </div>
      </footer>
    </div>
  );
}

function PortalSkeleton() {
  return (
    <div className="p-10 space-y-4 max-w-5xl mx-auto">
      <div className="h-8 w-48 bg-zinc-100 rounded animate-pulse" />
      <div className="h-6 w-80 bg-zinc-100 rounded animate-pulse" />
      <div className="grid sm:grid-cols-2 gap-4 mt-8">
        <div className="h-40 bg-zinc-50 rounded-2xl animate-pulse" />
        <div className="h-40 bg-zinc-50 rounded-2xl animate-pulse" />
      </div>
    </div>
  );
}

// ──── Utils ────
function isImage(url) {
  return /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(url || "");
}
function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}
